import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { printingTechData } from '../data/mock';

export default function PrintingTech() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <section ref={ref} className="relative py-32 overflow-hidden">
      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <div className="text-[#6B8FCC]/60 font-mono text-sm mb-4">TECHNOLOGIES</div>
          <h2 className="text-5xl md:text-7xl font-black mb-6 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
            3D Printing
            <br />
            Technologies
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Advanced additive manufacturing solutions for every application
          </p>
        </motion.div>

        {/* Tech Cards Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto"
        >
          {printingTechData.map((tech, index) => (
            <motion.div
              key={tech.id}
              variants={cardVariants}
              className="group relative"
            >
              {/* Card */}
              <div className="relative h-full p-6 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 hover:border-[#4A6FA5]/50 transition-all duration-500 hover:scale-105 cursor-pointer">
                {/* Tech Badge */}
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#4A6FA5]/10 border border-[#4A6FA5]/30 mb-4">
                  <div className="w-2 h-2 bg-[#6B8FCC] rounded-full animate-pulse" />
                  <span className="text-xs font-mono text-[#6B8FCC]">{tech.name}</span>
                </div>

                {/* Full Name */}
                <h3 className="text-xl font-bold mb-2 text-white group-hover:text-[#6B8FCC] transition-colors">
                  {tech.fullName}
                </h3>

                {/* Overview */}
                <p className="text-sm text-gray-400 mb-4 leading-relaxed">
                  {tech.overview}
                </p>

                {/* Specs */}
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Accuracy:</span>
                    <span className="text-[#6B8FCC] font-mono">{tech.accuracy}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Surface:</span>
                    <span className="text-[#6B8FCC] font-mono">{tech.surfaceFinish}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Lead Time:</span>
                    <span className="text-[#6B8FCC] font-mono">{tech.leadTime}</span>
                  </div>
                </div>

                {/* Materials */}
                <div className="pt-4 border-t border-white/10">
                  <div className="text-xs text-gray-500 mb-2">Materials:</div>
                  <div className="flex flex-wrap gap-1">
                    {tech.materials.slice(0, 3).map((material, idx) => (
                      <span key={idx} className="text-xs px-2 py-1 rounded bg-white/5 text-gray-400">
                        {material}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Hover Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#4A6FA5]/0 to-[#2A4A85]/0 group-hover:from-[#4A6FA5]/10 group-hover:to-[#2A4A85]/10 rounded-2xl transition-all duration-500 pointer-events-none" />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
